import { capturePrivateSession, assertPrivateSessionLifetime } from "@/lib/api/private-session";
import { usePrivateToast } from "@/shared/usePrivateToast";
import { bindPrivateApi } from "@/shared/usePrivateApi";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { invalidateInvoiceMutation } from "@/shared/invalidation";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";

import { ApiError } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import { useI18n, useTranslate } from "@/lib/i18n";
import { createBrowserPrintProvider, createQzPrintProvider } from "@/lib/printing";
import type { RestaurantPrintDeviceSession, RestaurantPrintJob, RestaurantPrintRenderModel } from "@/lib/api/types";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { sendCommand } from "@/shared/invoices/invoiceCommands";
import { renderCoordinatorArtifact } from "./coordinatorArtifact";
import { paperLogoDataUrl } from "@/lib/printing/paperLogo";
import { useStoreLogo } from "@/shared/useStoreLogo";
import { processClaimedPrintJob, type CoordinatorJobApi, type PrintJobAck } from "./printCoordinatorEngine";
import { startPrintCoordinatorOwner } from "./printCoordinatorOwnership";
import { subscribeRestaurantRealtime } from "@/shared/realtime";
import { retryRealtimeConnection } from "@a2z/realtime/firebase";
import { useOptionalRestaurantNotifications } from "@/modules/notifications/NotificationsProvider";

type CoordinatorState = "INERT" | "READY" | "PROCESSING" | "ATTENTION";
type CoordinatorContextValue = {
  ownsAutomaticPrinting: boolean;
  state: CoordinatorState;
};

const CoordinatorContext = createContext<CoordinatorContextValue>({
  ownsAutomaticPrinting: false,
  state: "INERT",
});

export function usePrintCoordinator() {
  return useContext(CoordinatorContext);
}

const HEARTBEAT_MS = 5_000;
/**
 * How often an UNATTENDED paired print computer re-attaches a failed live
 * listener. The shared recovery stops after its 1/5/15 s attempts and then
 * waits for focus/online/a tap — which never comes on a PC left alone at the
 * pass. This is a Firestore re-attach (the shared retry event), not an HTTP
 * poll; only with no Firebase at all does the event mean one catch-up read.
 */
export const REALTIME_REATTACH_MS = 120_000;
/**
 * Gate P7 rework 2 (R1) — how often the computer that OWNS printing tells the
 * server it is there (`POST …/device/presence`). The till stands its own chit
 * down only while a print computer was heard from in the last two minutes
 * (`deviceServed`), so a running but idle computer must be heard: 45 s, which
 * a throttled background tab stretches to at most a minute — one missed beat
 * still fits the window. It is a write of `last_seen_at`, never a claim, and
 * the server writes it on every beat: backlog P4.9 throttles the stamp on the
 * device's other calls (every 15 s at most), never on this one.
 */
export const PRESENCE_MS = 45_000;
const MAX_TRANSIENT_RETRIES = 3;

type PrintClaimQueue = { jobs: RestaurantPrintJob[]; nextWakeAt: string | null; serverTime: string };

function authorizationFailure(error: unknown): error is ApiError {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

function retryableFailure(error: unknown) {
  return !(error instanceof ApiError) || error.status === 429 || error.status >= 500;
}

export type PrintCoordinatorRuntime = ReturnType<typeof createPrintCoordinatorRuntime>;

export function createPrintCoordinatorRuntime(input: {
  recover(isActive: () => boolean): Promise<boolean | void>;
  claim(): Promise<RestaurantPrintJob[] | PrintClaimQueue>;
  heartbeat(job: RestaurantPrintJob): Promise<unknown>;
  /** R1 — "this computer is here", every `PRESENCE_MS` while active. */
  presence?(): Promise<unknown>;
  process(job: RestaurantPrintJob, onSendingAcknowledged: () => void): Promise<void>;
  onState(state: CoordinatorState): void;
  refreshSession?(): Promise<void>;
  /** Cancel preparation; only already-started SENDING must retain ownership. */
  stopPending?(): boolean;
  onAuthorizationLost?(): void;
  setTimer(callback: () => void, delay: number): ReturnType<typeof setTimeout>;
  clearTimer(timer: ReturnType<typeof setTimeout>): void;
}) {
  let active = false;
  let stopped = false;
  let failures = 0;
  let refreshedAuthorization = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let heartbeat: ReturnType<typeof setTimeout> | null = null;
  let presenceTimer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> | null = null;
  let wakeRequested = false;
  let nextWakeDelay: number | null = null;

  const schedule = (delay: number) => {
    if (!active) return;
    timer = input.setTimer(() => { timer = null; void tick(); }, delay);
  };

  // R1 — independent of the drain: a failed beat waits for the next one (a
  // refused device is the claim path's to discover), and it stops with the
  // runtime, so a departed owner or a refused pairing is silent.
  const schedulePresence = () => {
    if (!active || !input.presence) return;
    presenceTimer = input.setTimer(() => {
      presenceTimer = null;
      if (!active || !input.presence) return;
      void input.presence().catch(() => undefined).finally(schedulePresence);
    }, PRESENCE_MS);
  };

  const run = async () => {
    input.onState("READY");
    const recovered = await input.recover(() => active);
    if (!active) return;
    const claimed = await input.claim();
    const jobs = Array.isArray(claimed) ? claimed : claimed.jobs;
    const deadline = Array.isArray(claimed) || !claimed.nextWakeAt ? NaN : Date.parse(claimed.nextWakeAt) - Date.parse(claimed.serverTime);
    nextWakeDelay = Number.isFinite(deadline) && deadline > 0 ? Math.min(deadline + 25, 2_147_000_000) : null;
    for (const job of jobs) {
      if (!active) break;
      input.onState("PROCESSING");
      let processing = true;
      let leaseActive = true;
      let heartbeatError: unknown;
      const endLeaseHeartbeat = () => {
        leaseActive = false;
        if (heartbeat) input.clearTimer(heartbeat);
        heartbeat = null;
        // SENDING durably releases the CLAIMED lease. A concurrent heartbeat
        // can therefore refuse that old lease without invalidating the device.
        if (!authorizationFailure(heartbeatError)) heartbeatError = undefined;
      };
      const scheduleHeartbeat = () => {
        if (!active || !processing || !leaseActive || (heartbeatError && !retryableFailure(heartbeatError))) return;
        heartbeat = input.setTimer(() => {
          heartbeat = null;
          if (!active || !processing || !leaseActive) return;
          void input
            .heartbeat(job)
            .then(() => { heartbeatError = undefined; })
            .catch((error: unknown) => {
              if (!processing) return;
              if (error instanceof ApiError && error.status === 409 && error.code === "RESTAURANT_PRINTING_STATE_CONFLICT") {
                // An expired/released job lease is local to this job. The
                // SENDING acknowledgment still fences provider dispatch.
                endLeaseHeartbeat();
                return;
              }
              if (leaseActive || authorizationFailure(error)) heartbeatError = error;
            })
            .finally(scheduleHeartbeat);
        }, HEARTBEAT_MS);
      };
      scheduleHeartbeat();
      try {
        await input.process(job, endLeaseHeartbeat);
        // A completed preflight failure/unknown result also ends this lease.
        // Keep authorization failures actionable, discard transient old-job errors.
        endLeaseHeartbeat();
        if (heartbeatError) throw heartbeatError;
      } finally {
        processing = false;
        if (heartbeat) input.clearTimer(heartbeat);
        heartbeat = null;
      }
    }
    if (active) input.onState("READY");
    return Boolean(recovered) || jobs.length > 0;
  };

  const tick = (): Promise<void> => {
    if (!active) return Promise.resolve();
    if (inFlight) return inFlight;
    if (timer) input.clearTimer(timer);
    timer = null;
    wakeRequested = false;
    nextWakeDelay = null;
    let delay: number | null = null;
    let didWork = false;
    inFlight = run()
      .then((worked) => { failures = 0; refreshedAuthorization = false; didWork = Boolean(worked); })
      .catch(async (error: unknown) => {
        if (!active) return;
        input.onState("ATTENTION");
        if (error instanceof DOMException && error.name === "AbortError") {
          active = false;
          return;
        }
        if (authorizationFailure(error)) {
          if (error.status === 401 && !refreshedAuthorization && input.refreshSession) {
            refreshedAuthorization = true;
            try {
              await input.refreshSession();
              wakeRequested = true;
              return;
            } catch (refreshError) {
              if (!active) return;
              if (!authorizationFailure(refreshError)) {
                refreshedAuthorization = false;
                if (++failures <= MAX_TRANSIENT_RETRIES) delay = 3_000 * 2 ** failures;
                return;
              }
            }
          }
          active = false;
          input.onAuthorizationLost?.();
          return;
        }
        if (!retryableFailure(error)) {
          active = false;
          return;
        }
        if (++failures <= MAX_TRANSIENT_RETRIES) delay = 3_000 * 2 ** failures;
      })
      .finally(() => {
        inFlight = null;
        if (active && (didWork || wakeRequested)) void tick();
        else if (delay !== null) schedule(delay);
        else if (nextWakeDelay !== null) schedule(nextWakeDelay);
      });
    return inFlight;
  };

  return {
    start() {
      if (active || stopped) return;
      active = true;
      schedulePresence();
      void tick();
    },
    stop() {
      active = false;
      stopped = true;
      if (timer) input.clearTimer(timer);
      if (heartbeat) input.clearTimer(heartbeat);
      if (presenceTimer) input.clearTimer(presenceTimer);
      timer = null;
      heartbeat = null;
      presenceTimer = null;
      const mustDrain = input.stopPending?.() ?? true;
      return mustDrain ? inFlight ?? Promise.resolve() : Promise.resolve();
    },
    tick,
    wake() {
      if (!active) return;
      if (inFlight) wakeRequested = true;
      else { failures = 0; void tick(); }
    },
  };
}

function csrfHeaders(csrfToken: string) {
  return { "x-restaurant-print-csrf": csrfToken };
}

function coordinatorApi(csrf: () => string, queryClient: QueryClient, failure: { current: unknown }): CoordinatorJobApi & {
  request: ReturnType<typeof bindPrivateApi>["apiFetch"];
  claim(): Promise<RestaurantPrintJob[] | PrintClaimQueue>;
  pendingCommands(): Promise<RestaurantPrintJob[]>;
  heartbeat(job: RestaurantPrintJob): Promise<unknown>;
  presence(): Promise<unknown>;
  reset(): void;
  stopPending(): boolean;
  refreshSession(signal: AbortSignal): Promise<RestaurantPrintDeviceSession>;
} {
  const bound = bindPrivateApi();
  const pending = new Set<AbortController>();
  let stopped = false;
  let sending = false;
  const request = async <T,>(action: (signal: AbortSignal) => Promise<T>, signal?: AbortSignal | null): Promise<T> => {
    if ((stopped && !sending) || signal?.aborted) throw new DOMException("Print preparation stopped", "AbortError");
    const controller = new AbortController();
    pending.add(controller);
    let timedOut = false;
    const abort = () => controller.abort();
    signal?.addEventListener("abort", abort, { once: true });
    const deadline = setTimeout(() => { timedOut = true; abort(); }, 10_000);
    try {
      return await Promise.race([
        new Promise<never>((_resolve, reject) => controller.signal.addEventListener("abort", () => {
          reject(new DOMException(timedOut ? "Print request timed out" : "Print preparation stopped", timedOut ? "TimeoutError" : "AbortError"));
        }, { once: true })),
        action(controller.signal),
      ]);
    } finally {
      clearTimeout(deadline);
      signal?.removeEventListener("abort", abort);
      pending.delete(controller);
    }
  };
  const apiFetch: typeof bound.apiFetch = async (path, options) => {
    if (authorizationFailure(failure.current)) throw failure.current;
    try {
      return await request((signal) => bound.apiFetch(path, { ...options, signal }), options?.signal);
    } catch (error) {
      if (authorizationFailure(error) || retryableFailure(error)) failure.current ??= error;
      throw error;
    }
  };
  const lifetime = capturePrivateSession();
  return {
    request: apiFetch,
    reset: () => { sending = false; failure.current = undefined; },
    stopPending: () => {
      stopped = true;
      if (!sending) for (const controller of pending) controller.abort();
      return sending;
    },
    refreshSession: (signal) => request((requestSignal) => bound.apiFetch<RestaurantPrintDeviceSession>(endpoints.restaurant.printDeviceSession, { signal: requestSignal }), signal),
    claim: () =>
      apiFetch<RestaurantPrintJob[] | PrintClaimQueue>(endpoints.restaurant.claimPrintJobs, {
        method: "POST",
        headers: csrfHeaders(csrf()),
        body: { limit: 1, includeQueueState: true },
      }),
    pendingCommands: () => apiFetch<RestaurantPrintJob[]>(endpoints.restaurant.pendingPrintCommands),
    heartbeat: (job) =>
      // The runtime owns heartbeat errors by job lifetime. A completed job's
      // late response must not poison the next job's API failure latch.
      request((signal) => bound.apiFetch(endpoints.restaurant.heartbeatPrintJob(job.id), {
        method: "POST",
        headers: csrfHeaders(csrf()),
        body: { leaseId: job.leaseId },
        signal,
      })),
    // Like the lease heartbeat, outside the failure latch: a lost beat must
    // not fail the next drain.
    presence: () =>
      request((signal) => bound.apiFetch(endpoints.restaurant.printDevicePresence, {
        method: "POST",
        headers: csrfHeaders(csrf()),
        signal,
      })),
    render: (jobId, leaseId) => apiFetch<RestaurantPrintRenderModel>(endpoints.restaurant.renderPrintJob(jobId, leaseId)),
    acknowledge: (jobId, body: PrintJobAck) => {
      if (body.status === "SENDING") {
        if (stopped) return Promise.reject(new DOMException("Print preparation stopped", "AbortError"));
        // From this request onward its outcome may be durable; drain instead of cancelling it.
        sending = true;
      }
      return apiFetch(endpoints.restaurant.acknowledgePrintJob(jobId), {
        method: "POST",
        headers: csrfHeaders(csrf()),
        body,
      });
    },
    advanceKitchen: async ({ jobId, leaseId, commandId, ticketId, invoiceId }) => {
      assertPrivateSessionLifetime(lifetime);
      const result = await request((signal) => sendCommand(
        {
          id: commandId,
          type: "printTicket",
          invoiceId,
          payload: { ticketId },
        },
        {
          signal,
          headers: {
            "x-restaurant-print-job-id": jobId,
            "x-restaurant-print-csrf": csrf(),
            ...(leaseId ? { "x-restaurant-print-lease-id": leaseId } : {}),
          },
        },
      ));
      await invalidateInvoiceMutation(queryClient, result.invoice?.id ?? invoiceId);
      return result;
    },
    completeKitchenAdvance: (jobId) =>
      apiFetch(endpoints.restaurant.completePrintCommand(jobId), {
        method: "POST",
        headers: csrfHeaders(csrf()),
      }),
  };
}

export function PrintCoordinator({ children }: { children: ReactNode }) {
  const toast = usePrivateToast();
  const t = useTranslate();
  const queryClient = useQueryClient();
  const { locale } = useI18n();
  const [state, setState] = useState<CoordinatorState>("INERT");
  const [paperOwner, setPaperOwner] = useState<PrintCoordinatorRuntime | null>(null);
  const [suspendedToken, setSuspendedToken] = useState<string | null>(null);
  const csrf = useRef("");
  const session = useApiQuery<RestaurantPrintDeviceSession>(queryKeys.printDeviceSession, endpoints.restaurant.printDeviceSession, {
    retry: false,
    networkMode: "always",
    staleTime: Infinity,
    // Focus/reconnection may discover repaired pairing; a refusal never restarts its interval.
    refetchOnWindowFocus: (query) => authorizationFailure(query.state.error) || query.state.data?.csrfToken === suspendedToken ? "always" : true,
    refetchOnReconnect: (query) => authorizationFailure(query.state.error) || query.state.data?.csrfToken === suspendedToken ? "always" : true,
  });
  const csrfToken = session.data?.csrfToken ?? "";
  const paired = Boolean(session.data) && !authorizationFailure(session.error) && csrfToken !== suspendedToken;
  const pairedDeviceId = paired ? session.data?.device.id ?? null : null;
  const pairedStoreId = session.data?.device.storeId;
  const refetchDeviceSession = session.refetch;
  const notifications = useOptionalRestaurantNotifications();
  const realtimeDown = notifications?.realtimeStatus === "degraded" ||
    notifications?.realtimeStatus === "unconfigured" || notifications?.bootstrapStatus === "unavailable";
  useEffect(() => {
    // Paired devices only, so a staff phone in a pocket never runs it; a paired
    // PC runs it hidden too, because a background tab is how a print PC sits.
    // It dispatches the same event as the notice's Try again: never the claim
    // endpoint or a session read of its own. Live again, or unpaired, clears it.
    if (!pairedDeviceId || !realtimeDown) return;
    const timer = setInterval(retryRealtimeConnection, REALTIME_REATTACH_MS);
    return () => clearInterval(timer);
  }, [pairedDeviceId, realtimeDown]);
  useEffect(() => subscribeRestaurantRealtime((event) => {
    if (!pairedDeviceId && event.catchUp) void refetchDeviceSession();
  }), [pairedDeviceId, refetchDeviceSession]);
  useEffect(() => { csrf.current = csrfToken; }, [csrfToken]);
  // The store's logo for the check and the receipt, read only on a paired
  // print computer and embedded per job (`paperLogoDataUrl` caches it).
  const storeLogoUrl = useStoreLogo(Boolean(pairedDeviceId));

  const renderContext = useRef({ locale, t, storeLogoUrl, toast });
  useEffect(() => { renderContext.current = { locale, t, storeLogoUrl, toast }; }, [locale, t, storeLogoUrl, toast]);

  useEffect(() => {
    if (!pairedDeviceId) return;
    const requestFailure: { current: unknown } = { current: undefined };
    const api = coordinatorApi(() => csrf.current, queryClient, requestFailure);
    const providers = {
      browser: createBrowserPrintProvider(),
      qz: createQzPrintProvider({
        security: {
          certificate: async () => {
            const response = await api.request<{ certificate: string }>(endpoints.restaurant.qzCertificate);
            return response.certificate;
          },
          authorizeOperation: async ({ operation, jobLease, queueId, copies, dataFormat }) =>
            api.request<{ authorizationToken: string }>(endpoints.restaurant.authorizeQzPrintJob(jobLease.jobId), {
              method: "POST",
              headers: csrfHeaders(csrf.current),
              body: {
                leaseId: jobLease.leaseId,
                operation,
                queueId,
                ...(copies === undefined ? {} : { copies }),
                ...(dataFormat === undefined ? {} : { dataFormat }),
              },
            }),
          authorize: async ({ operation, payload, jobLease, queueId, copies, dataFormat, authorizationToken }) => {
            const response = await api.request<{ signature: string }>(endpoints.restaurant.signQzPrintJob(jobLease.jobId), {
              method: "POST",
              headers: csrfHeaders(csrf.current),
              body: {
                leaseId: jobLease.leaseId,
                operation,
                queueId,
                ...(copies === undefined ? {} : { copies }),
                ...(dataFormat === undefined ? {} : { dataFormat }),
                authorizationToken,
                payload,
              },
            });
            return response.signature;
          },
        },
      }),
    };
    const runtime: PrintCoordinatorRuntime = createPrintCoordinatorRuntime({
      recover: async (isActive) => {
        api.reset();
        const pending = await api.pendingCommands();
        let completed = false;
        for (const job of pending) {
          if (!isActive()) break;
          const command = job.preissuedPrintCommand;
          if (!command) continue;
          try {
            await api.advanceKitchen({
              jobId: job.id,
              commandId: command.id,
              ticketId: command.payload.ticketId,
              invoiceId: command.invoiceId,
            });
            if (!isActive()) break;
            await api.completeKitchenAdvance(job.id);
            completed = true;
          } catch (error) {
            if (authorizationFailure(error) || retryableFailure(error)) throw error;
            // The durable marker remains for the next revision or recovery wake. A stuck command
            // must not prevent unrelated stations from claiming new paper.
          }
        }
        return completed;
      },
      claim: api.claim,
      heartbeat: api.heartbeat,
      presence: api.presence,
      process: async (job, onSendingAcknowledged) => {
        api.reset();
        const { locale, t, storeLogoUrl, toast } = renderContext.current;
        const result = await processClaimedPrintJob({
          job,
          api,
          providers,
          onSendingAcknowledged,
          renderArtifact: async (model) =>
            renderCoordinatorArtifact({
              model,
              locale,
              t,
              logoDataUrl:
                model.job.kind === "PRE_BILL_CHECK" || model.job.kind === "CUSTOMER_RECEIPT"
                  ? await paperLogoDataUrl(storeLogoUrl)
                  : null,
            }),
        });
        if (result.status === "UNKNOWN") {
          toast.error(t((m) => m.manage.printOutcomeUnknown));
        } else if (result.status === "FAILED") {
          toast.error(t((m) => m.manage.printJobFailed));
        } else if ("kitchenAdvanceFailed" in result && result.kitchenAdvanceFailed) {
          toast.error(t((m) => m.manage.printKitchenAdvancePending));
        } else if ("usedFallback" in result && result.usedFallback) {
          toast.success(t((m) => m.manage.printBrowserFallbackUsed));
        }
        if (requestFailure.current) throw requestFailure.current;
      },
      refreshSession: async () => {
        const refreshed = await queryClient.fetchQuery({
          queryKey: queryKeys.printDeviceSession,
          queryFn: ({ signal }) => api.refreshSession(signal),
          staleTime: 0,
          retry: false,
          networkMode: "always",
        });
        if (refreshed.device.id !== pairedDeviceId) throw new DOMException("Print device changed", "AbortError");
        csrf.current = refreshed.csrfToken;
      },
      stopPending: api.stopPending,
      onAuthorizationLost: () => {
        void queryClient.cancelQueries({ queryKey: queryKeys.printDeviceSession, exact: true });
        setSuspendedToken(csrf.current);
      },
      onState: (next) => {
        setState(next);
        if (next === "PROCESSING") setPaperOwner(runtime);
        else setPaperOwner((owner) => owner === runtime ? null : owner);
      },
      setTimer: (callback, delay) => setTimeout(callback, delay),
      clearTimer: (timer) => clearTimeout(timer),
    });
    const stopOwner = startPrintCoordinatorOwner(runtime, pairedDeviceId, navigator.locks, () => setState("ATTENTION"));
    const onOnline = () => runtime.wake();
    const unsubscribe = subscribeRestaurantRealtime((event) => {
      if (event.storeId === pairedStoreId && (event.catchUp || event.topics.includes("printing"))) runtime.wake();
    });
    window.addEventListener("online", onOnline);
    window.addEventListener("focus", onOnline);
    return () => {
      unsubscribe();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", onOnline);
      stopOwner();
      void runtime.stop().then(() => setPaperOwner((owner) => owner === runtime ? null : owner));
    };
  }, [pairedDeviceId, pairedStoreId, queryClient]);

  const ownsAutomaticPrinting = paired || paperOwner !== null;
  return <CoordinatorContext.Provider value={{ ownsAutomaticPrinting, state: ownsAutomaticPrinting ? state : "INERT" }}>{children}</CoordinatorContext.Provider>;
}
